import ConditionalRender from 'base/ConditionalRender/ConditionalRender';
import {
  ApplicationBillRecipient,
  ApplicationData,
  FileDetails,
  OptionI
} from '../../../../../types/interface';
import { t } from 'i18next';
import {
  capitalizeFirstLetter,
  formatDateOnly,
  convertToCurrency,
  parseIntoNumber,
  showValue
} from 'utils/helpers';
import { WITH_INCOME, WITHOUT_INCOME } from 'utils/enums/misc';
import { StandardImageUpload } from 'base/StandardImageUpload/StandardImageUpload';
import { ColumnDef } from '@tanstack/react-table';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import { Dropdown } from 'react-bootstrap';
import { UilEllipsisV } from '@iconscout/react-unicons';

export const HealthApplication = ({
  data,
  basic_data,
  submitted_on,
  requestOptions
}: {
  data: ApplicationData;
  basic_data: typeof WITHOUT_INCOME | typeof WITH_INCOME;
  submitted_on: string;
  requestOptions: OptionI[];
}) => {

  const columns: ColumnDef<ApplicationBillRecipient>[] = [
    {
      id: 'option',
      accessorKey: t('overview.health_application.table_header.text_10'),
      header: () => (
        <span className="text-first-capitalize">
          {capitalizeFirstLetter(
            t('overview.health_application.table_header.text_10')
          )}
        </span>
      ),
      cell: ({ row: { original } }) => {
        return (
          <div className="">
            {
              requestOptions.filter(
                option => parseInt(option?.value as string) === original?.option
              )[0]?.label
            }
          </div>
        );
      },
      footer: () => <span></span>
    },
    {
      id: 'bill_recipient_name',
      accessorKey: t('overview.health_application.table_header.text_1'),
      header: () => (
        <span className="text-first-capitalize">
          {capitalizeFirstLetter(
            t('overview.health_application.table_header.text_1')
          )}
        </span>
      ),
      cell: ({ row: { original } }) => {
        return <div className="">{original?.bill_recipient_name}</div>;
      },
      footer: () => <span></span>
    },
    {
      id: 'invoice_issuer',
      accessorKey: t('overview.health_application.table_header.text_2'),
      header: () => (
        <span className="text-first-capitalize">
          {capitalizeFirstLetter(
            t('overview.health_application.table_header.text_2')
          )}
        </span>
      ),
      cell: ({ row: { original } }) => {
        return <div className="">{original?.invoice_issuer}</div>;
      },
      footer: () => <span></span>
    },
    {
      id: 'bill_number',
      accessorKey: t('overview.health_application.table_header.text_3'),
      header: () => (
        <span className="text-first-capitalize">
          {capitalizeFirstLetter(
            t('overview.health_application.table_header.text_3')
          )}
        </span>
      ),
      cell: ({ row: { original } }) => {
        return <div className="">{original?.bill_number}</div>;
      },
      footer: () => <span></span>
    },
    {
      id: 'total_invoice',
      accessorKey: t('overview.health_application.table_header.text_4'),
      header: () => (
        <span className="text-first-capitalize">
          {capitalizeFirstLetter(
            t('overview.health_application.table_header.text_4')
          )}
        </span>
      ),
      cell: ({ row: { original } }) => {
        return (
          <div className="">
            {convertToCurrency(
              original.total_invoice ? original.total_invoice : 0
            )}
          </div>
        );
      },
      footer: () => (
        <div>
          <span>
            {capitalizeFirstLetter(
              t('overview.health_application.footer.text_1')
            )}{' '}
            :{' '}
          </span>
          <span className="font-bold">
            {convertToCurrency(
              data?.bill_recipients?.reduce(
                (a, b) => parseIntoNumber(a) + parseIntoNumber(b.total_invoice),
                0
              ) as number
            )}
          </span>
        </div>
      )
    },
    {
      id: 'date_invoice',
      accessorKey: t('overview.health_application.table_header.text_5'),
      header: () => (
        <span className="text-first-capitalize">
          {capitalizeFirstLetter(
            t('overview.health_application.table_header.text_5')
          )}
        </span>
      ),
      cell: ({ row: { original } }) => {
        return <div className="">{formatDateOnly(original?.date_invoice)}</div>;
      },
      footer: () => <span></span>
    },
    {
      id: 'paid_date',
      accessorKey: t('overview.health_application.table_header.text_6'),
      header: () => (
        <span className="text-first-capitalize">
          {capitalizeFirstLetter(
            t('overview.health_application.table_header.text_6')
          )}
        </span>
      ),
      cell: ({ row: { original } }) => {
        return <div className="">{formatDateOnly(original?.paid_date)}</div>;
      },
      footer: () => <span></span>
    },
    {
      id: 'restoration_svt_to_eur',
      accessorKey: t('overview.health_application.table_header.text_7'),
      header: () => (
        <span className="text-first-capitalize">
          {capitalizeFirstLetter(
            t('overview.health_application.table_header.text_7')
          )}
        </span>
      ),
      cell: ({ row: { original } }) => {
        return (
          <div className="">
            {convertToCurrency(
              parseIntoNumber(original?.restoration_insurance) +
              parseIntoNumber(original?.restoration_svt_to_eur)
            )}
          </div>
        );
      },
      footer: () => (
        <div>
          <span>
            {capitalizeFirstLetter(
              t('overview.health_application.footer.text_2')
            )}{' '}
            :{' '}
          </span>
          <span className="font-bold">
            {convertToCurrency(
              data?.bill_recipients?.reduce(
                (a, b) =>
                  parseIntoNumber(a) +
                  (parseIntoNumber(b.restoration_insurance) +
                    parseIntoNumber(b.restoration_svt_to_eur)),
                0
              ) as number
            )}
          </span>
        </div>
      )
    },
    {
      id: 'product_total',
      accessorKey: t('overview.health_application.table_header.text_8'),
      header: () => (
        <span className="text-first-capitalize">
          {capitalizeFirstLetter(
            t('overview.health_application.table_header.text_8')
          )}
        </span>
      ),
      cell: ({ row: { original } }) => {
        return (
          <div className="">
            {convertToCurrency(original.sum_minus_insurance || 0)}
          </div>
        );
      },
      footer: () => (
        <div>
          <span>
            {capitalizeFirstLetter(
              t('overview.health_application.footer.text_3')
            )}{' '}
            :{' '}
          </span>
          <span className="font-bold">
            {convertToCurrency(
              data?.bill_recipients?.reduce(
                (a, b) =>
                  parseIntoNumber(a) + parseIntoNumber(b.sum_minus_insurance),
                0
              ) as number
            )}
          </span>
        </div>
      )
    },
    {
      id: 'bill_recipient_sum',
      accessorKey: t('overview.health_application.table_header.text_9'),
      header: () => (
        <span className="text-first-capitalize">
          {capitalizeFirstLetter(
            t('overview.health_application.table_header.text_9')
          )}
        </span>
      ),
      cell: ({ row: { original } }) => {
        return (
          <div className="">
            {convertToCurrency(
              showValue(original?.bill_recipient_sum as string)
            )}
          </div>
        );
      },
      footer: () => (
        <div>
          <span>
            {capitalizeFirstLetter(
              t('overview.health_application.footer.text_4')
            )}{' '}
            :{' '}
          </span>
          <span className="font-bold">
            {convertToCurrency(
              data?.bill_recipients?.reduce(
                (a, b) => a + parseIntoNumber(b.bill_recipient_sum),
                0
              ) as number
            )}
          </span>
        </div>
      )
    },
    {
      id: 'submission_date',
      accessorKey: t('general.common_text.text_22'),
      header: () => {
        return (
          <span className="text-first-capitalize">
            {t('general.common_text.text_22')}
          </span>
        );
      },
      cell: () => {
        return <div>{formatDateOnly(submitted_on)}</div>;
      }
    },
    {
      id: 'documentation',
      accessorKey: t('overview.health_application.table_header.text_11'),
      header: () => (
        <span className="text-first-capitalize">
          {capitalizeFirstLetter(
            t('overview.health_application.table_header.text_11')
          )}
        </span>
      ),
      cell: ({ row: { original } }) => {
        return (
          <Dropdown
            id="scroll_in_table"
            placement="auto"
            className="position-static"
          >
            <Dropdown.Toggle
              variant="link"
              className="no-padding no-margin ps-0 d-flex"
            >
              <UilEllipsisV className="text-body" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item as={'div'}>
                <StandardImageUpload
                  disabled={false}
                  imageText={t('application.general.text_5')}
                  value={original.invoice}
                  label={t('application.general.text_5')}
                />
              </Dropdown.Item>
              <Dropdown.Item as={'div'}>
                <StandardImageUpload
                  disabled={false}
                  imageText={t('application.general.text_27')}
                  value={original.payment_confirmation}
                  label={t('application.general.text_27')}
                />
              </Dropdown.Item>
              <Dropdown.Item as={'div'}>
                <StandardImageUpload
                  disabled={false}
                  imageText={t('application.general.text_3')}
                  value={original.social_security_provider_refund}
                  label={t('application.general.text_3')}
                />
              </Dropdown.Item>
              <ConditionalRender
                condition={!!original?.other_funds?.original_file_name}
              >
                <Dropdown.Item as={'div'}>
                  <StandardImageUpload
                    disabled={false}
                    imageText={t('application.general.text_4')}
                    value={original.other_funds}
                    label={t('application.general.text_4')}
                  />
                </Dropdown.Item>
              </ConditionalRender>
            </Dropdown.Menu>
          </Dropdown>
        );
      },
      footer: () => <span></span>
    }
  ];

  const table = useAdvanceTable({
    data: data?.bill_recipients as ApplicationBillRecipient[],
    columns: columns,
    sortable: false
  });

  return (
    <div id="application-health" className="mt-5">
      <div className="row">
        <div className="col-12 mb-5">
          <h1 className="text-center">
            {capitalizeFirstLetter(t('overview.general.text_2'))}
          </h1>
        </div>
      </div>
      <ConditionalRender
        condition={!!data?.bill_recipients && data.bill_recipients.length > 0}
      >
        <AdvanceTableProvider {...table}>
          <AdvanceTable
            tableProps={{
              responsive: true,
              className: 'phoenix-table fs-9 p-0',
              striped: true
            }}
            dataLength={data?.bill_recipients?.length}
            hasFooter={true}
          />
        </AdvanceTableProvider>
      </ConditionalRender>
    </div>
  );
};
